/**
 * Language Context
 *
 * Global context for managing application language.
 * Syncs with i18next and provides a simple API for components.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Language,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  LANGUAGE_NAMES,
  LANGUAGE_FLAGS,
  LOCALE_STRINGS,
} from '../i18n/config';

interface LanguageContextType {
  /** Current language code */
  language: Language;
  /** Change the application language */
  setLanguage: (lang: Language) => void;
  /** Toggle between available languages */
  toggleLanguage: () => void;
  /** Check if a language is supported */
  isSupported: (lang: string) => lang is Language;
  /** Get display name for a language */
  getLanguageName: (lang: Language) => string;
  /** Get flag emoji for a language */
  getLanguageFlag: (lang: Language) => string;
  /** Get locale string (e.g., 'es-ES', 'en-US') */
  getLocaleString: (lang?: Language) => string;
  /** List of supported languages */
  supportedLanguages: readonly Language[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();

  const [language, setLanguageState] = useState<Language>(() => {
    // Get initial language from i18n (which already detected from localStorage/navigator)
    const detectedLang = i18n.language?.split('-')[0]; // Handle 'en-US' -> 'en'
    if (detectedLang && SUPPORTED_LANGUAGES.includes(detectedLang as Language)) {
      return detectedLang as Language;
    }
    return DEFAULT_LANGUAGE;
  });

  // Sync with i18n on mount
  useEffect(() => {
    const currentLang = i18n.language?.split('-')[0];
    if (currentLang !== language) {
      i18n.changeLanguage(language);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      console.warn(`Language "${lang}" is not supported. Using default.`);
      lang = DEFAULT_LANGUAGE;
    }

    setLanguageState(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [i18n]);

  const toggleLanguage = useCallback(() => {
    const currentIndex = SUPPORTED_LANGUAGES.indexOf(language);
    const nextIndex = (currentIndex + 1) % SUPPORTED_LANGUAGES.length;
    setLanguage(SUPPORTED_LANGUAGES[nextIndex]);
  }, [language, setLanguage]);

  const isSupported = useCallback((lang: string): lang is Language => {
    return SUPPORTED_LANGUAGES.includes(lang as Language);
  }, []);

  const getLanguageName = useCallback((lang: Language): string => {
    return LANGUAGE_NAMES[lang] || lang;
  }, []);

  const getLanguageFlag = useCallback((lang: Language): string => {
    return LANGUAGE_FLAGS[lang] || '';
  }, []);

  const getLocaleString = useCallback((lang?: Language): string => {
    return LOCALE_STRINGS[lang || language] || LOCALE_STRINGS[DEFAULT_LANGUAGE];
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    toggleLanguage,
    isSupported,
    getLanguageName,
    getLanguageFlag,
    getLocaleString,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access the language context
 * @throws Error if used outside of LanguageProvider
 */
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Hook to get current language without full context
 * Safe to use outside provider (returns default language)
 */
export function useCurrentLanguage(): Language {
  const context = useContext(LanguageContext);
  return context?.language || DEFAULT_LANGUAGE;
}
